import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Drawer, Timeline, Spin, Empty } from 'antd';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { TimelineWrapper } from 'components/styled/scoped';
import dayjs from 'lib/dayjs';
import { getOfficialScenarioLogs } from 'services/api/pureLLM';
import { useResolveTodoTypeLabel } from 'views/PureLLM/Hooks/useResolveTodoTypeLabel';
import { getReceptionModeOptionLabel } from './ReceptionModeDrawer';
import type { ApiReceptionMode } from 'views/PureLLM/Types/api';
import { OfficialScenarioLogOperationType } from 'views/PureLLM/Types/api';

export interface OperationLogItem {
  /** 操作人名称 */
  operatorName: string;
  /** 操作描述 */
  description: string;
  /** 操作时间（时间戳） */
  createTime?: number;
}

export interface OperationLogDrawerProps {
  visible: boolean;
  onClose: () => void;
  region?: string;
  storeGroupId?: string | number;
  intentId?: string;
  language?: string;
}

const LOG_PAGE_SIZE = 20;

function normalizeScenarioId(id: string | number) {
  if (typeof id === 'number') return id;
  return /^\d+$/.test(id) ? Number(id) : id;
}

function parseBooleanLike(value?: string | boolean) {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;

  const normalizedValue = value.toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === '1') return true;
  if (normalizedValue === 'false' || normalizedValue === '0') return false;

  return undefined;
}

function getEnableText(value: string | boolean | undefined, t: (key: string) => string) {
  const enable = parseBooleanLike(value);
  if (enable === undefined) return '-';
  return enable ? t('marketing.开启') : t('marketing.关闭');
}

function getReceptionModeText(
  receptionMode?: ApiReceptionMode,
  todoType?: string,
  t?: (key: string) => string,
  resolveTodoTypeLabel?: (todoType?: string) => string | undefined,
): string {
  if (!receptionMode) return '-';

  return t
    ? getReceptionModeOptionLabel(t, receptionMode, todoType, resolveTodoTypeLabel)
    : receptionMode;
}

function OperationLogDrawer(props: OperationLogDrawerProps) {
  const { visible, onClose, region, storeGroupId, intentId, language } = props;
  const { t } = useTranslate();
  const resolveTodoTypeLabel = useResolveTodoTypeLabel();
  const baseQueryParams = useMemo(
    () => ({
      region: region || '',
      storeGroupId: normalizeScenarioId(storeGroupId || ''),
      intentId: normalizeScenarioId(intentId || ''),
      pageSize: LOG_PAGE_SIZE,
      language,
    }),
    [intentId, region, storeGroupId, language],
  );
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['pureLLM', 'operationLogs', baseQueryParams],
    queryFn: ({ pageParam = 0, signal }) =>
      getOfficialScenarioLogs(
        {
          ...baseQueryParams,
          offsetId: pageParam,
        },
        signal,
      ),
    getNextPageParam: (lastPage) => {
      const hasNext = parseBooleanLike(lastPage.hasNext);

      if (!hasNext) {
        return undefined;
      }

      return lastPage.nextOffsetId ?? undefined;
    },
    enabled: visible && Boolean(region) && storeGroupId !== undefined && Boolean(intentId),
  });
  const logs = useMemo<OperationLogItem[]>(
    () =>
      (data?.pages || [])
        .flatMap((page) => page.data || [])
        .map((item) => {
          const userName = item.userId === '-1' ? t('common.nickname.le') : item.userName || '-';
          const intentName = item.intentName || item.intentId || '-';
          const policyName = item.policyName || item.policyId || '-';

          let description = t('scene.aiAgentConfig.operationLog.description.unknownOperation', {
            userName,
            intentName,
            policyName,
          });

          if (item.operateType === OfficialScenarioLogOperationType.UPDATE_SCENARIO_SWITCH) {
            description = t('scene.aiAgentConfig.operationLog.description.updateScenarioSwitch', {
              userName,
              intentName,
              before: getEnableText(item.updateScenarioContent?.before?.enable, t),
              after: getEnableText(item.updateScenarioContent?.after?.enable, t),
            });
          } else if (item.operateType === OfficialScenarioLogOperationType.UPDATE_POLICY_SWITCH) {
            description = t('scene.aiAgentConfig.operationLog.description.updatePolicySwitch', {
              userName,
              intentName,
              policyName,
              before: getEnableText(item.updatePolicyContent?.before?.enable, t),
              after: getEnableText(item.updatePolicyContent?.after?.enable, t),
            });
          } else if (
            item.operateType === OfficialScenarioLogOperationType.UPDATE_RECEPTION_METHOD
          ) {
            description = t('scene.aiAgentConfig.operationLog.description.updateReceptionMethod', {
              userName,
              intentName,
              policyName,
              before: getReceptionModeText(
                item.updateSubPolicyContent?.before?.receptionMode as ApiReceptionMode | undefined,
                item.updateSubPolicyContent?.before?.todoType,
                t,
                resolveTodoTypeLabel,
              ),
              after: getReceptionModeText(
                item.updateSubPolicyContent?.after?.receptionMode as ApiReceptionMode | undefined,
                item.updateSubPolicyContent?.after?.todoType,
                t,
                resolveTodoTypeLabel,
              ),
            });
          }

          return {
            operatorName: '',
            description,
            createTime:
              typeof item.createTime === 'number' ? item.createTime : Number(item.createTime),
          };
        }),
    [data, resolveTodoTypeLabel, t],
  );

  return (
    <Drawer
      data-sid="old-drawer-01"
      title={t('scene.aiAgentConfig.operationLog.title')}
      placement="right"
      width={528}
      visible={visible}
      onClose={onClose}
      className={drawerStyle}
    >
      {isLoading ? (
        <div data-sid="old-loading-06" className="flex items-center justify-center py-12">
          <Spin />
        </div>
      ) : !logs.length ? (
        <div data-sid="old-empty-07" className="py-12">
          <Empty description={t('scene.aiAgentConfig.operationLog.empty.noData')} />
        </div>
      ) : (
        <div data-sid="old-content-08" className="flex flex-col gap-4">
          <TimelineWrapper data-sid="old-timeline-02">
            {logs.map((log, index) => (
              <Timeline.Item key={index}>
                <div data-sid="old-item-03" className="flex flex-col">
                  <div
                    data-sid="old-desc-04"
                    className="text-sm text-[var(--fett-text-primary)] leading-5 mb-1"
                  >
                    {[log.operatorName, log.description].filter(Boolean).join(' ')}
                  </div>
                  {log.createTime ? (
                    <div
                      data-sid="old-time-05"
                      className="text-xs text-[var(--fett-text-tertiary)]"
                    >
                      {dayjs(log.createTime).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                  ) : null}
                </div>
              </Timeline.Item>
            ))}
          </TimelineWrapper>
          {hasNextPage ? (
            <div data-sid="old-loadmore-09" className="flex justify-center">
              <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                {t('scene.aiAgentConfig.operationLog.action.loadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}

const drawerStyle = css`
  .ant-drawer-body {
    padding: 16px 24px;
  }
`;

export default OperationLogDrawer;
